<script setup>
import moment from 'moment'
import { onMounted, ref, watch, watchEffect, nextTick, reactive, computed } from 'vue'
import MetricWidget from '@/components/UI/MetricWidget'
import LineChart from '@/components/Dashboard/Chart/line.vue'
import { useAxiosClient, useFetch } from '@/composable/useFetch'
import { i18n } from '@/i18n'
import { useStore } from '@/store/index';
import ReportChartByBusiness from '@/components/Dashboard/ReporCharts/Business.vue';
import ReportChartBySalesChannel from '@/components/Dashboard/ReporCharts/SalesChannel.vue';
import ReportChartByChannel from '@/components/Dashboard/ReporCharts/Channel.vue';
import ReportChartByWarehouse from '@/components/Dashboard/ReporCharts/Warehouse.vue';
import ReportChartByArea from '@/components/Dashboard/ReporCharts/Area.vue';
import ReportChartByProduct from '@/components/Dashboard/ReporCharts/Product.vue';
import BizMultiSelect from '@/components/UI/Input/BizMultiSelect.vue'
import { useRoute } from 'vue-router/composables'

const route = useRoute()
const store = useStore()

const workspaceId = computed(() => store.state.user.workspace_id_active)
const workspaceBusinessId = computed(() => route.query.business_id || '')

const currencyList = {
  IDR: {
    valueRatio: 100_000,
  },
  MYR: {
    valueRatio: 10,
  },
}

const warehouses = useAxiosClient(
  '/warehouse/query/list',
  {},
  {
    watch: [workspaceId],
    initialData: () => [],
    immediate: true,
    transform: response => response?.data?.data || [],
  },
)

const warehouseOptions = computed(
  () => (warehouses.state.value || [])
    .filter(warehouse => !workspaceBusinessId.value || warehouse.business_id === workspaceBusinessId.value)
    .map(warehouse => ({
      value: warehouse.warehouse_id,
      label: `[${warehouse.business_title}] ${warehouse.name}`,
    }))
    .toSorted((a, b) => a.label.localeCompare(b.label)),
)
const selectedWarehouse = ref([])
const selectedWarehouseIds = computed(() => {
  const warehouseId = route.query.warehouse_id
  if (warehouseId) {
    return typeof warehouseId === 'string' ? [warehouseId] : Array.from(warehouseId)
  }
  return Array.isArray(selectedWarehouse.value) ? selectedWarehouse.value : []
})
const selectedWarehouseFallback = computed(() => selectedWarehouseIds.value
  .map(id => warehouses.state.value.find(warehouse => warehouse.warehouse_id === id))
  .filter(Boolean),
)
watch(workspaceBusinessId, () => {
  selectedWarehouse.value = []
})

function arrayToString(array, defaultValue = undefined) {
  if (typeof array === 'string') return array

  return array?.length ? array.join(',') : defaultValue
}

const summaryOrderStatus = useAxiosClient(
  '/bi/dashboard2/dash_ord_date/chart/order_status',
  () => ({
    params: {
      warehouse_ids: arrayToString(selectedWarehouseIds.value),
      business_ids: arrayToString(workspaceBusinessId.value),
    },
  }),
  {
    watch: [selectedWarehouseIds, workspaceId, workspaceBusinessId],
    initialData: () => {},
    immediate: true,
  },
)

const summaryOrderStatusComputed = computed(() => ({
  new: {
    title: i18n.t('dashboard.order_status.new'),
    value: summaryOrderStatus.state.value.new || summaryOrderStatus.state.value.new_ || 0,
    color: '33,150,243',
    route: { name: 'fulfillment.index', query: { ...route.query, delivery_order_states: 'created,confirmed' } },
  },
  on_process: {
    title: i18n.t('dashboard.order_status.on_process'),
    value: summaryOrderStatus.state.value.on_process || 0,
    color: '7, 190, 154',
    route: { name: 'fulfillment.index', query: { ...route.query, delivery_order_states: 'accepted' } },
  },
  ready_to_ship: {
    title: i18n.t('dashboard.order_status.ready_to_ship'),
    value: summaryOrderStatus.state.value.ready_to_ship || 0,
    color: '114, 46, 209',
    route: { name: 'fulfillment.index', query: { ...route.query, delivery_order_states: 'good_issued' } },
  },
  cancel_requested: {
    title: i18n.t('dashboard.order_status.cancel_requested'),
    value: summaryOrderStatus.state.value.cancel_requested || 0,
    color: '245, 34, 45',
    route: { path: '/sales', query: { ...route.query, sales_state: 'CANCELREQUESTED', filter_category: 'cancelrequested', date_from: 'all', date_until: 'all' } },
  },
}))

const inventoryItemCount = useAxiosClient(
  '/inventoryv2/query/dash/sku',
  () => ({
    params: { 
      warehouse_ids: arrayToString(selectedWarehouseIds.value),
      business_ids: arrayToString(workspaceBusinessId.value),
    },
  }),
  {
    watch: [selectedWarehouseIds, workspaceId, workspaceBusinessId],
    initialData: () => 0,
    immediate: true,
    transform: data => +data || 0,
  },
)

const inventoryOosCount = useAxiosClient(
  '/inventoryv2/query/dash/sku',
  () => ({
    params: { 
      warehouse_ids: arrayToString(selectedWarehouseIds.value),
      business_ids: arrayToString(workspaceBusinessId.value),
      status: 'OUT_OF_STOCK',
    },
  }),
  {
    watch: [selectedWarehouseIds, workspaceId, workspaceBusinessId],
    initialData: () => 0,
    immediate: true,
    transform: data => +data || 0,
  },
)

const pendingInboundCount = useAxiosClient(
  '/goodreceive/query/statistics/purchase-orders',
  () => ({
    params: { 
      business_ids: arrayToString(workspaceBusinessId.value),
      warehouse_ids: arrayToString(selectedWarehouseIds.value),
      status: 'PENDING',
    },
  }),
  {
    watch: [selectedWarehouseIds, workspaceId, workspaceBusinessId],
    initialData: () => 0,
    immediate: true,
    transform: response => {
      return (response?.data || []).reduce((total, item) => {
        if (item.status === 'FULL_RECEIVED') return total;

        return total + item.count;
      }, 0)
    },
  },
)


const channelsCount = useAxiosClient(
  '/bi/dashboard2/dash_ord_date/channels', 
  () => ({
    params: {
      business_ids: arrayToString(workspaceBusinessId.value),
    },
  }),
  {
    initialData: [],
    immediate: true,
    watch: () => [workspaceBusinessId.value, workspaceId.value],
    transform(data) {
      return data?.length || 0
    },
  },
)


const DEFAULT_STATISTICS_FILTER_VALUE = () => ({
  start_date: null,
  end_date: null,
  currency_code: 'IDR',
  business_ids: route.query.business_id || null,
  warehouse_ids: route.query.warehouse_id || null,
  sales_channel_ids: null,
  channel_ids: null,
})
const selectedCurrency = computed(() => currencyList[statisticsFilter.currency_code] ?? currencyList.IDR)
const statisticsFilter = reactive(DEFAULT_STATISTICS_FILTER_VALUE())
watch(workspaceId, () => {
  Object.assign(statisticsFilter, DEFAULT_STATISTICS_FILTER_VALUE())
})

const selectedBusinessIds = computed(() => statisticsFilter.business_ids?.split(',') || [])

watch(selectedWarehouseFallback, (values) => {
  const validBusinessId = selectedBusinessIds.value.filter(businessId => !!values?.find(warehouse => warehouse.business_id === businessId))

  statisticsFilter.business_ids = validBusinessId.length ? validBusinessId?.join(',') : (route.query.business_id || null)
})

const businesses = computed(() => {
  const list =  store.state.user.businessList;
  const selectedBusiness = route.query.business_id;
  const validBusinessBySelectedWarehouse = selectedWarehouseFallback.value?.map(warehouse => warehouse.business_id);

  const filtered = list.filter(item => {
    if (item.business_id === 'all') return false

    let valid = selectedBusiness ? item.business_id === selectedBusiness : true;

    // if (validBusinessBySelectedWarehouse.length) {
    //   valid = valid && validBusinessBySelectedWarehouse.includes(item.business_id)
    // }

    return valid
  })

  return (filtered.length ? filtered : list).toSorted((a, b) => {
    const nameA = a.business_name || a.name || ''
    const nameB = b.business_name || b.name || ''
    return nameA.localeCompare(nameB)
  })
})

const filterWarehouseOptions = computed(() => {
  return warehouses.state.value
    .filter(item => {
      let valid = selectedBusinessIds.value.length ? selectedBusinessIds.value.includes(item.business_id) : true

      if (valid && selectedWarehouse.value.length) {
        valid = valid && selectedWarehouse.value.includes(item.warehouse_id)
      }

      return valid
    })
    .map(item => ({
      value: item.warehouse_id,
      label: item.name,
    }))
    .toSorted((a, b) => a.label.localeCompare(b.label))
})

const salesChannels = useAxiosClient(
  '/bi/dashboard2/dash_ord_date/sales_channels', 
  () => ({
    params: {
      business_ids: statisticsFilter.business_ids || undefined,
    },
  }),
  {
    initialData: [],
    immediate: true,
    watch: () => [statisticsFilter.business_ids, workspaceId.value, workspaceBusinessId.value],
  },
)
const filterSalesChannelOptions = computed(() => salesChannels.state.value.map(item => ({
  value: item.id,
  label: item.channelName,
})))

const channels = useAxiosClient(
  '/bi/dashboard2/dash_ord_date/channels', 
  () => ({
    params: {
      business_ids: statisticsFilter.business_ids || undefined,
    },
  }),
  {
    initialData: [],
    immediate: true,
    watch: () => [statisticsFilter.business_ids, workspaceId.value, workspaceBusinessId.value],
  },
)

const filterChannelOptions = computed(() => channels.state.value.map(item => ({
  value: item.id,
  label: item.channelTitle,
})))


const dateLabel = ref(null)
const periodePlaceholders = computed(() => [
  {
    title: i18n.t('dashboard.today'),
    value: [moment().startOf('day'), moment().endOf('day')],
  },
  {
    title: i18n.tc('dashboard.last_n_days', 7),
    value: [moment().subtract(7, 'days').startOf('day'), moment().endOf('day')],
  },
  {
    title: i18n.t('dashboard.mtd'),
    value: [moment().startOf('month'), moment().endOf('day')],
  },
  {
    title: i18n.t('dashboard.last_month'),
    value: [
      moment().subtract(1, 'month').startOf('month'),
      moment().subtract(1, 'month').endOf('month'),
    ],
  },
  {
    title: i18n.t('dashboard.ytd'),
    value: [moment().startOf('year'), moment().endOf('day')],
  },
  {
    title: i18n.tc('dashboard.n_year', 1),
    value: [moment().subtract(1, 'year').startOf('day'), moment().endOf('day')],
  },
  {
    title: i18n.t('dashboard.last_year'),
    value: [
      moment().subtract(1, 'year').startOf('year'),
      moment().subtract(1, 'year').endOf('year'),
    ],
  },
])

/**
 * @param {(import('moment').Moment|string)[]} date
 */
function setStatisticFilterDate(date, label = null) {
  statisticsFilter.start_date = moment.isMoment(date[0]) ? date[0].format('YYYY-MM-DD') : date[0]
  statisticsFilter.end_date = moment.isMoment(date[1]) ? date[1].format('YYYY-MM-DD') : date[1]

  const startDate = moment(statisticsFilter.start_date).startOf('day')
  const endDate = moment(statisticsFilter.end_date).endOf('day')

  dateLabel.value =
    label ||
    periodePlaceholders.value.find(
      item =>
        item.value[0].isSame(startDate) &&
        item.value[1].isSame(endDate),
    )?.title ||
    null
}

watch(workspaceId, () => {
  setStatisticFilterDate([moment().subtract(7, 'days'), moment()])
}, { immediate: true })

function removeArrayItem(array, item) {
  const arrayValues = typeof array === 'string' ? array.split(',') : array
  return (arrayValues || []).filter(el => el !== item)
}
function deselectItem(prop, item) {
  const value = removeArrayItem(prop, item)
  return arrayToString(value, null)
}

function formatNumber(value, currency = true, compact = false, fractionDigits = null) {
  return i18n.n(
    value || 0, { 
      currency: statisticsFilter.currency_code,
      style: currency ? 'currency' : 'decimal',
      notation: compact ? 'compact' : 'standard',
      maximumFractionDigits: compact ? 1 : (fractionDigits ?? (statisticsFilter.currency_code === 'IDR' ? 0 : 2)),
      minimumFractionDigits: 0,
    },
  )
}

function abbrNumber(value, currency = true) {
  return formatNumber(value, currency, true)
}

const todaySummary = useAxiosClient(
  '/bi/dashboard2/dash_ord_date/chart/summary',
  () => ({
    params: {
      start_date: moment().startOf('day').format('YYYY-MM-DD'),
      end_date: moment().endOf('day').format('YYYY-MM-DD'),
      currency_code: statisticsFilter.currency_code,
      warehouse_ids: arrayToString(selectedWarehouse.value),
      business_ids: workspaceBusinessId.value,
    },
  }),
  {
    watch: () => [statisticsFilter.currency_code, selectedWarehouse.value, workspaceId.value, workspaceBusinessId.value],
    immediate: true,
  },
)

const summary = useAxiosClient(
  '/bi/dashboard2/dash_ord_date/chart/summary',
  () => ({
    params: statisticsFilter,
  }),
  {
    watch: [statisticsFilter, workspaceId, workspaceBusinessId],
    immediate: true,
  },
)

const salesOverviewTime = computed(() => {
  const start = moment(statisticsFilter.start_date)
  const end = moment(statisticsFilter.end_date)

  const timeDiffInDays = end.diff(start, 'days')

  return {
    start_date: start.subtract(timeDiffInDays < 7 ? 7 : 0, 'days').format('YYYY-MM-DD'),
    end_date: end.format('YYYY-MM-DD'),
    timeDiffInDays,
  }
})
const salesTimeRange = ref('daily')
const salesOverview = useAxiosClient(
  '/bi/dashboard2/dash_ord_date/chart/sales_overview',
  () => ({ params: { ...statisticsFilter, ...salesOverviewTime.value } }),
  { 
    watch: [statisticsFilter, salesOverviewTime, workspaceId, workspaceBusinessId], 
    immediate: true,
    beforeExecute() {
      salesTimeRange.value = 'daily'
    },

    onSuccess(res) {
      salesTimeRange.value = res?.[0]?.dataType || 'daily'
    },
  },
)

function getWeekLabel(dateStr, firstDate, lastDate) {
  const m = moment.isMoment(dateStr) ? dateStr : moment(`${dateStr}`);
  let start = m.clone().startOf("week");
  let end = m.clone().endOf("week");
  const first = moment(`${firstDate}`);
  const last = moment(`${lastDate}`);

  if (first.isAfter(start)) {
    start = first
  }

  if (last.isBefore(end)) {
    end = last
  }

  if (start.month() === end.month()) {
    return `${start.format("D")}-${end.format("D MMM YY")}`;
  }

  return `${start.format("D MMM")}-${end.format("D MMM YY")}`;
}

const salesChart = computed(() => {
  // /** @type {Record<string, { gmv: number[], nmv: number[], orders: number[] }} */
  // const records = {};

  /** @type {string[]} */
  const labels = [];
  /** @type {Record<string, number>} */
  const gmv = {};
  /** @type {Record<string, number>} */
  const nmv = {};
  /** @type {Record<string, number>} */
  const orders = {};
  /** @type {Record<string, number>} */
  const items = {};

  const values = (salesOverview.state.value || []).reduce((acc, item) => {
    acc[item.dateInt] = item;

    return acc;
  }, {});
  const startDate = moment(salesOverviewTime.value.start_date)
  const endDate = moment(salesOverviewTime.value.end_date)

  for (let i = startDate; i.isSameOrBefore(endDate); i.add(1, salesTimeRange.value === 'monthly' ? 'month' : 'day')) {
    const item = values[i.format(salesTimeRange.value === 'monthly' ? 'YYYYMM' : 'YYYYMMDD')];

    if (salesTimeRange.value === 'weekly') {
      const key = getWeekLabel(i, salesOverviewTime.value.start_date, salesOverviewTime.value.end_date);

      if (!labels.includes(key)) labels.push(key);
      gmv[key] = (gmv[key] || 0) + (item?.salesAggr || 0);
      nmv[key] = (nmv[key] || 0) + (item?.nmvAggr || 0);
      orders[key] = (orders[key] || 0) + (item?.totalOrderAggr || 0);
      items[key] = (items[key] || 0) + (item?.totalQtyAggr || 0);
    } else {
      const key = i.format(salesTimeRange.value === 'daily' ? 'DD MMM YY' : 'MMM YY');

      labels.push(key);
      gmv[key] = item?.salesAggr || 0;
      nmv[key] = item?.nmvAggr || 0;
      orders[key] = item?.totalOrderAggr || 0;
      items[key] = item?.totalQtyAggr || 0;
    }
    
  }

  const borderWidth = labels.length > 30 ? 3 : 4;
  const pointRadius = labels.length > 30 ? 2 : 4;

  const valueRatio = selectedCurrency.value.valueRatio ?? 100_000

  return {
    labels: labels,
    datasets: [
      {
        label: valueRatio  === 1 ? 'GMV' : `GMV (1:${abbrNumber(valueRatio , false)})`,
        _tooltipValue: Object.values(gmv).map(v => `GMV: ${formatNumber(v, true)}`),
        data: Object.values(gmv).map(v => v / valueRatio ),
        backgroundColor: '#FF0A5480',
        borderColor: '#FF0A5480',
        borderWidth,
        pointRadius,
        fill: false,
      },
      {
        label: valueRatio  === 1 ? 'GMV' : `NMV (1:${abbrNumber(valueRatio , false)})`,
        _tooltipValue: Object.values(nmv).map(v => `NMV: ${formatNumber(v, true)}`),
        data: Object.values(nmv).map(v => v / valueRatio ),
        backgroundColor: '#007bff80',
        borderColor: '#007bff80',
        borderWidth,
        pointRadius,
        fill: false,
      },
      {
        label: i18n.t('dashboard.item_sold'),
        _tooltipValue: Object.values(items).map(v => i18n.t('dashboard.item_sold_count', { n: v, label: formatNumber(v, false)})),
        data: Object.values(items),
        backgroundColor: '#07beb8aa',
        borderColor: '#07beb8aa',
        borderWidth,
        pointRadius,
        fill: false,
        hidden: true,
      },
      {
        label: i18n.t('dashboard.total_order_text'),
        _tooltipValue: Object.values(orders).map(v => `${v} orders`),
        data: Object.values(orders),
        backgroundColor: '#595c9770',
        borderColor: '#595c9770',
        borderWidth,
        pointRadius,
        fill: false,
      },
    ],
  }
})
const salesChartConfig = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { position: 'bottom' },
  scales: {
    xAxes: [
      {
        gridLines: {
          display: true,
          color: '#e4e9f005',
          drawTicks: true,
          zeroLineWidth: 1,
          zeroLineColor: '#e4e9f0',
        },
        ticks: {
          fontColor: '#a1a1c2',
        },
      },
    ],
    yAxes: [
      {
        gridLines: {
          color: '#e4e9f080',
          drawTicks: true,
          zeroLineColor: '#e4e9f0',
        },
        ticks: {
          fontColor: '#a1a1c2',
        },
      },
    ],
  },
  tooltips: { 
    mode: 'x', 
    position: 'nearest', 
    intersect: false,
    titleMarginBottom: 12,
    bodySpacing: 8,
    callbacks: {
      label: (t, d) => {
        return d.datasets[t.datasetIndex]._tooltipValue?.[t.index] || t.value
      },
    },
  },
}

const colorPalette = [
  '#FF0A54aa',
  '#007BFFaa',
  // '#28a745aa',
  '#07beb8dd',
  '#FFC10799',
  '#503D9Aaa',
  '#43CBA4aa',
  '#a9baff',
  '#f8c4d0',
  '#D3ECB6',
  '#CCCCCC',
]

// const topCouriers = useAxiosClient(
//   '/bi/dashboard2/dash_ord_date/chart/order_by_courier',
//   () => ({ params: statisticsFilter }),
//   { watch: [statisticsFilter], immediate: true },
// )

// const topPaymentMethods = useAxiosClient(
//   '/bi/dashboard2/dash_ord_date/chart/order_by_payment_method',
//   () => ({ params: statisticsFilter }),
//   { watch: [statisticsFilter], immediate: true },
// )


</script>

<template>
  <div class="dashboard-v3">
    <a-alert type="info" class="mb-4">
      <template #message>
        <div class="pt-2" v-html="$i18n.t('dashboard.new_version_welcome_message')" />

        <div class="mb-2">
          <router-link :to="{ path: '/dashboard/old', query: $route.query }">
            {{ $t('dashboard.new_version_welcome_message_link') }}
          </router-link>
        </div>
      </template>
    </a-alert>

    <BizMultiSelect
      :key="`wahouse-select-${workspaceBusinessId}`"
      :default-value="selectedWarehouse"
      mode="multiple"
      allow-clear
      show-search
      :placeholder="$t('topBar.all_Warehouse')"
      :options="warehouseOptions"
      :max-tag-count="1"
      :loading="false"
      :disabled="false"
      option-filter-prop="children"
      :class="{ 'w-100': true, 'is-active': selectedWarehouse?.length }"
      @blur="(values) => JSON.stringify(values) === JSON.stringify(selectedWarehouse) ? null : selectedWarehouse = values"
      @deselect="(value) => selectedWarehouse = removeArrayItem(selectedWarehouse, value)"
    />

    <div class="summary-section mb-4 mt-4">
      <div class="order-status" style="grid-area: order">
        <h2>{{ $t('dashboard.order_status.title') }}</h2>
        <div class="text-muted mb-2 small">
          {{ $t('dashboard.order_status.description') }}
        </div>

        <div class="ant-row" style="margin-top: auto">
          <div class="ant-col-24">
            <ul>
              <li
                v-for="item in summaryOrderStatusComputed"
                :key="item.title"
                :style="{ '--color': item.color }"
              >
                <router-link :to="item.route">
                  <a-skeleton
                    :loading="summaryOrderStatus.isLoading.value"
                    active
                    :title="true"
                    :paragraph="false"
                  >
                    <div class="order-status__value">
                      <span>
                        {{ item.value }}
                      </span>
                    </div>
                  </a-skeleton>
                  <div class="order-status__title">
                    {{ item.title }}
                  </div>
                </router-link>
              </li>
            </ul>
          </div>

          <div class="ant-col-12" />
        </div>
      </div>
      <MetricWidget
        :label="$t('dashboard.inventory_item.title')"
        :description="$t('dashboard.inventory_item.description')"
        :loading="inventoryItemCount.isLoading.value"
        rgb-color="7, 190, 154"
      >
        <template #value>
          <a-tooltip placement="bottom" :title="formatNumber(inventoryItemCount.state.value, false)">
            {{ abbrNumber(inventoryItemCount.state.value, false) }}
          </a-tooltip>
        </template>
        <template #icon>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36">
            <path
              fill="currentColor"
              d="m33.53 18.76l-6.93-3.19V6.43a1 1 0 0 0-.6-.9l-7.5-3.45a1 1 0 0 0-.84 0l-7.5 3.45a1 1 0 0 0-.58.91v9.14l-6.9 3.18a1 1 0 0 0-.58.91v9.78a1 1 0 0 0 .58.91l7.5 3.45a1 1 0 0 0 .84 0l7.08-3.26l7.08 3.26a1 1 0 0 0 .84 0l7.5-3.45a1 1 0 0 0 .58-.91v-9.78a1 1 0 0 0-.57-.91m-2.81.91L25.61 22l-5.11-2.33l5.11-2.35ZM18.1 4.08l5.11 2.35l-5.11 2.35L13 6.43Zm-7.5 13.23l5.11 2.35L10.6 22l-5.11-2.33Zm6.5 11.49l-6.5 3l-6.5-3v-7.57L10.38 24a1 1 0 0 0 .82 0l6.08-2.8Zm-5.5-13.23V8l6.08 2.8a1 1 0 0 0 .84 0L24.6 8v7.58l-6.5 3Zm20.51 13.24l-6.5 3l-6.51-3v-7.59L25.19 24a1 1 0 0 0 .81 0l6.08-2.8Z"
              class="clr-i-outline clr-i-outline-path-1"
            />
            <path fill="none" d="M0 0h36v36H0z" />
          </svg>
        </template>
      </MetricWidget>
      <MetricWidget
        :label="$t('dashboard.oos_item.title')"
        :loading="inventoryOosCount.isLoading.value"
        :description="$t('dashboard.oos_item.description')"
      >
        <template #value>
          <a-tooltip placement="bottom" :title="formatNumber(inventoryOosCount.state.value, false)">
            {{ abbrNumber(inventoryOosCount.state.value, false) }}
          </a-tooltip>
        </template>
        <template #icon>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <g
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            >
              <path
                d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14M7.5 4.27l9 5.15"
              />
              <path d="M3.29 7L12 12l8.71-5M12 22V12m5 1l5 5m-5 0l5-5" />
            </g>
          </svg>
        </template>
      </MetricWidget>
      <MetricWidget
        :label="$t('dashboard.connected_store.title')"
        :value="channelsCount.state.value || 0"
        :loading="channelsCount.isLoading.value"
        :description="$t('dashboard.connected_store.description')"
        rgb-color="33,150,243"
      >
        <template #icon>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <g
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            >
              <circle cx="18" cy="18" r="3" />
              <circle cx="6" cy="6" r="3" />
              <path d="M13 6h3a2 2 0 0 1 2 2v7m-7 3H8a2 2 0 0 1-2-2V9" />
            </g>
          </svg>
        </template>
      </MetricWidget>
      <MetricWidget
        :label="$t('dashboard.pending_po.title')"
        :loading="pendingInboundCount.isLoading.value"
        :value="pendingInboundCount.state.value || 0"
        :description="$t('dashboard.pending_po.description')"
        rgb-color="255,193,7"
      >
        <template #icon>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14">
            <g
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect
                width="6.5"
                height="6.5"
                x="5.5"
                y="3"
                rx="1"
              />
              <path
                d="M4 12h7.61a1 1 0 0 1 .7.29l1.19 1.21M.5.5h1a1 1 0 0 1 1 1V10M4 11.75A2.11 2.11 0 0 1 4 12a1.74 1.74 0 1 1 0-.25ZM8.5 7h1"
              />
            </g>
          </svg>
        </template>
      </MetricWidget>
    </div>

    <a-divider class="my-5" />
    <section id="today-summary" class="my-n3 pb-3">
      <div class="d-lg-flex mb-4 align-items-center">
        <div class="ml-auto mb-2 order-2">
          <a-select
            v-model="statisticsFilter.currency_code"
            :options="Object.keys(currencyList).map(code => ({ label: `${code} - ${$t('currency.' + code.toLowerCase())}`, value: code }))"
            class="currency-input"
          />
        </div>
        <h2 class="">
          {{ $t('dashboard.today_summary') }}
        </h2>
      </div>
      <a-row :gutter="[24, 32]" class="mt-n1">
        <a-col :span="24" :sm="{ span: 12 }" :xl="{ span: 6 }">
          <MetricWidget
            :label="$t('dashboard.gmv.title')"
            :loading="todaySummary.isLoading.value"
            value="0"
            rgb-color="125, 125, 125"
          >
            <template #value>
              <a-tooltip placement="bottom" :title="formatNumber(todaySummary.data.value?.salesAggr)">
                {{ abbrNumber(todaySummary.data.value?.salesAggr) }}
              </a-tooltip>
            </template>
            <template #description>
              <a-row :gutter="[24, 24]" class="">
                <a-col span="12"> 
                  <a-skeleton
                    :loading="todaySummary.isLoading.value"
                    :title="false"
                    :paragraph="{ rows: 1 }"
                    active
                    class="my-0"
                  >
                    {{ $tc('dashboard.total_orders', todaySummary.data.value?.totalOrderAggr, { value: formatNumber(todaySummary.data.value?.totalOrderAggr, false) }) }} 
                  </a-skeleton>
                </a-col>
                <a-col span="12 text-right"> 
                  <a-tooltip placement="bottom" :title="$t('dashboard.gmv.abv.long', { n: formatNumber(todaySummary.data.value?.salesAggr / todaySummary.data.value?.totalOrderAggr) })">
                    {{ $t('dashboard.gmv.abv.short', { n: abbrNumber(todaySummary.data.value?.salesAggr / todaySummary.data.value?.totalOrderAggr) }) }}
                  </a-tooltip>
                </a-col>
              </a-row>
            </template>
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <g fill="none" stroke="currentColor" stroke-width="1.5">
                  <path
                    stroke-linecap="round"
                    d="m8 16l8.72-.727c2.729-.227 3.341-.823 3.643-3.544L21 6M6 6h1.5M22 6h-3"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M10.5 7s1 0 2 2c0 0 3.177-5 6-6"
                  />
                  <circle cx="6" cy="20" r="2" />
                  <circle cx="17" cy="20" r="2" />
                  <path
                    stroke-linecap="round"
                    d="M8 20h7M2 2h.966c.945 0 1.768.625 1.997 1.515L7.94 15.076a1.96 1.96 0 0 1-.35 1.686L6.631 18"
                  />
                </g>
              </svg>
            </template>
          </MetricWidget>
        </a-col>
        <a-col :span="24" :sm="{ span: 12 }" :xl="{ span: 6 }">
          <MetricWidget
            :label="$t('dashboard.nmv.title')"
            :loading="todaySummary.isLoading.value"
            rgb-color="125, 125, 125"
          >
            <template #value>
              <a-tooltip placement="bottom" :title="formatNumber(todaySummary.data.value?.nmvAggr)">
                {{ abbrNumber(todaySummary.data.value?.nmvAggr) }}
              </a-tooltip>
            </template>
            <template #description>
              <a-row :gutter="[24, 24]" class="">
                <a-col span="12"> 
                  <a-skeleton
                    :loading="todaySummary.isLoading.value"
                    :title="false"
                    :paragraph="{ rows: 1 }"
                    active
                    class="my-0"
                  >
                    {{ $tc('dashboard.total_orders', todaySummary.data.value?.totalOrderAggr, { value: formatNumber(todaySummary.data.value?.totalOrderAggr, false) }) }} 
                  </a-skeleton>
                </a-col>
                <a-col span="12 text-right"> 
                  <a-tooltip placement="bottom" :title="$t('dashboard.nmv.abv.long', { n: formatNumber(todaySummary.data.value?.nmvAggr / todaySummary.data.value?.totalOrderAggr) })">
                    {{ $t('dashboard.nmv.abv.short', { n: abbrNumber(todaySummary.data.value?.nmvAggr / todaySummary.data.value?.totalOrderAggr) }) }}
                  </a-tooltip>
                </a-col>
              </a-row>
            </template>
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <g
                  fill="none"
                  stroke="currentColor"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                >
                  <path
                    stroke-linecap="round"
                    d="M20.943 16.835a15.76 15.76 0 0 0-4.476-8.616c-.517-.503-.775-.754-1.346-.986C14.55 7 14.059 7 13.078 7h-2.156c-.981 0-1.472 0-2.043.233c-.57.232-.83.483-1.346.986a15.76 15.76 0 0 0-4.476 8.616C2.57 19.773 5.28 22 8.308 22h7.384c3.029 0 5.74-2.227 5.25-5.165"
                  />
                  <path
                    d="M7.257 4.443c-.207-.3-.506-.708.112-.8c.635-.096 1.294.338 1.94.33c.583-.009.88-.268 1.2-.638C10.845 2.946 11.365 2 12 2s1.155.946 1.491 1.335c.32.37.617.63 1.2.637c.646.01 1.305-.425 1.94-.33c.618.093.319.5.112.8l-.932 1.359c-.4.58-.599.87-1.017 1.035S13.837 7 12.758 7h-1.516c-1.08 0-1.619 0-2.036-.164S8.589 6.38 8.189 5.8z"
                  />
                  <path
                    stroke-linecap="round"
                    d="M13.627 12.919c-.216-.799-1.317-1.519-2.638-.98s-1.53 2.272.467 2.457c.904.083 1.492-.097 2.031.412c.54.508.64 1.923-.739 2.304c-1.377.381-2.742-.214-2.89-1.06m1.984-5.06v.761m0 5.476v.764"
                  />
                </g>
              </svg>
            </template>
          </MetricWidget>
        </a-col>
        <a-col :span="24" :sm="{ span: 12 }" :xl="{ span: 6 }">
          <MetricWidget
            :label="$t('dashboard.logistic.title')"
            :loading="todaySummary.isLoading.value"
            rgb-color="125, 125, 125"
          >
            <template #value>
              <a-tooltip placement="bottom" :title="formatNumber(todaySummary.data.value?.logisticCost)">
                {{ abbrNumber(todaySummary.data.value?.logisticCost) }}
              </a-tooltip>
            </template>
            <template #description>
              <a-row :gutter="[24, 24]" class="">
                <!-- <a-col span="12"> 189772 Orders </a-col> -->
                <a-col class="text-left">
                  <a-tooltip :title="$t('dashboard.logistic.als.long', { n: formatNumber(todaySummary.data.value?.logisticCost / todaySummary.data.value?.totalOrderAggr) })">
                    {{ $t('dashboard.logistic.als.short', { n: formatNumber(todaySummary.data.value?.logisticCost / todaySummary.data.value?.totalOrderAggr) }) }}
                  </a-tooltip>
                </a-col>
              </a-row>
            </template>
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <g
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                >
                  <path
                    d="M2 21.494c1.295.096 2.384-.994 3.333-.994s2.492 1.005 3.334.994c1.01.009 2.193-.994 3.333-.994s2.323 1.003 3.333.994c1.295.096 2.384-.994 3.334-.994s2.491 1.005 3.333.994M6 20.5c-1.418-1.766-2.417-4.027-2.843-5.225c-.135-.38-.202-.57-.124-.752c.079-.181.27-.266.655-.436l7.49-3.318c.404-.18.607-.269.822-.269s.418.09.823.27l7.489 3.317c.384.17.576.255.655.436c.078.182.01.372-.124.752c-.426 1.198-1.425 3.459-2.843 5.225"
                  />
                  <path
                    d="m6 13l.216-2.807c.135-1.752.202-2.628.777-3.16c.575-.533 1.454-.533 3.211-.533h3.592c1.757 0 2.636 0 3.21.533c.576.532.643 1.408.778 3.16L18 13"
                  />
                  <path
                    d="m8.5 6.5l.172-1.372c.156-1.251.234-1.877.66-2.253c.425-.375 1.055-.375 2.316-.375h.704c1.26 0 1.891 0 2.316.375c.426.376.504 1.002.66 2.253L15.5 6.5"
                  />
                </g>
              </svg>
            </template>
          </MetricWidget>
        </a-col>
        <a-col :span="24" :sm="{ span: 12 }" :xl="{ span: 6 }">
          <MetricWidget
            :label="$t('dashboard.item_sold')"
            :value="formatNumber(todaySummary.data.value?.totalQtyAggr, false)"
            :loading="todaySummary.isLoading.value"
            :description="$t('dashboard.item_sold_desc')"
            rgb-color="125, 125, 125"
          >
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <g
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                >
                  <path d="m16 16l2 2l4-4" />
                  <path
                    d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14M7.5 4.27l9 5.15"
                  />
                  <path d="M3.29 7L12 12l8.71-5M12 22V12" />
                </g>
              </svg>
            </template>
          </MetricWidget>
        </a-col>
      </a-row>
    </section>

    <a-divider class="my-5" />

    <div class="statistics-overview-section mt-n3">
      <h2 class="mb-3">
        {{ $t('dashboard.statistics_overview') }}
      </h2>

      <div class="filter-form">
        <div class="date-fields">
          <ul>
            <li
              v-for="item in periodePlaceholders"
              :key="item.title"
              :class="{ active: dateLabel === item.title }"
              @click="setStatisticFilterDate(item.value, item.title)"
            >
              {{ item.title }}
            </li>

            <li class="date-picker">
              <a-range-picker
                :value="[
                  $moment(statisticsFilter.start_date),
                  $moment(statisticsFilter.end_date),
                ]"
                format="DD MMM YYYY"
                :disabled-date="
                  current => current && current > $moment().endOf('day')
                "
                :allow-clear="false"
                :placeholder="[
                  $t('dashboardContent.start_date'),
                  $t('dashboardContent.end_date'),
                ]"
                :class="{ 'is-active': !dateLabel }"
                @change="value => setStatisticFilterDate(value)"
              >
                <a-icon
                  slot="suffixIcon"
                  type="calendar"
                  style="color: #999999"
                />
              </a-range-picker>
            </li>
          </ul>
        </div>

        <a-row :gutter="[16, 8]" class="mt-3">
          <a-col :span="24" :sm="{ span: 12 }" :xl="{ span: 6 }">
            <BizMultiSelect
              :key="statisticsFilter.business_ids || 'business_id'"
              :default-value="selectedBusinessIds"
              mode="multiple"
              allow-clear
              show-search
              :placeholder="$t('topBar.all_Business')"
              :options="businesses.map(item => ({
                label: item.business_title || item.business_name,
                value: item.business_id
              }))"
              :max-tag-count="1"
              :loading="false"
              :disabled="false"
              option-filter-prop="children"
              :class="{ 'w-100': true, 'is-active': statisticsFilter.business_ids?.length }"
              @blur="(values) => statisticsFilter.business_ids = arrayToString(values, null)"
              @deselect="(value) => statisticsFilter.business_ids = deselectItem(statisticsFilter.business_ids, value)"
            />
          </a-col>
          <a-col :span="24" :sm="{ span: 12 }" :xl="{ span: 6 }">
            <BizMultiSelect
              :default-value="statisticsFilter.warehouse_ids?.split(',')"
              mode="multiple"
              allow-clear
              show-search
              :placeholder="$t('topBar.all_Warehouse')"
              :options="filterWarehouseOptions"
              :max-tag-count="1"
              :loading="false"
              :disabled="false"
              option-filter-prop="children"
              :class="{ 'w-100': true, 'is-active': statisticsFilter.warehouse_ids?.length }"
              @blur="(values) => statisticsFilter.warehouse_ids = arrayToString(values, null)"
              @deselect="(values) => statisticsFilter.warehouse_ids = deselectItem(statisticsFilter.warehouse_ids, values)"
            />
          </a-col>
          <a-col :span="24" :sm="{ span: 12 }" :xl="{ span: 6 }">
            <BizMultiSelect
              :default-value="statisticsFilter.sales_channel_ids?.split(',')"
              mode="multiple"
              allow-clear
              show-search
              :placeholder="$t('topBar.all_SalesChannel')"
              :options="filterSalesChannelOptions"
              :max-tag-count="1"
              :loading="false"
              :disabled="false"
              option-filter-prop="children"
              :class="{ 'w-100': true, 'is-active': statisticsFilter.sales_channel_ids?.length }"
              @blur="(values) => statisticsFilter.sales_channel_ids = arrayToString(values, null)"
              @deselect="(values) => statisticsFilter.sales_channel_ids = deselectItem(statisticsFilter.sales_channel_ids, values)"
            />
          </a-col>
          <a-col :span="24" :sm="{ span: 12 }" :xl="{ span: 6 }">
            <BizMultiSelect
              :default-value="statisticsFilter.channel_ids?.split(',')"
              mode="multiple"
              allow-clear
              show-search
              :placeholder="$t('topBar.all_Channel')"
              :options="filterChannelOptions"
              :max-tag-count="1"
              :loading="channels.isLoading.value"
              option-filter-prop="children"
              :class="{ 'w-100': true, 'is-active': statisticsFilter.channel_ids?.length }"
              @blur="(values) => statisticsFilter.channel_ids = arrayToString(values, null)"
              @deselect="(values) => statisticsFilter.channel_ids = deselectItem(statisticsFilter.channel_ids, values)"
            />
          </a-col>
        </a-row>
      </div>

      <a-row :gutter="[24, 32]" class="mt-5">
        <a-col :span="24" :lg="{ span: 12 }" :xl="{ span: 6 }">
          <MetricWidget 
            :label="$t('dashboard.gmv.title')"
            :loading="summary.isLoading.value"
          >
            <template #value>
              <a-tooltip placement="bottom" :title="formatNumber(summary.data.value?.salesAggr)">
                {{ abbrNumber(summary.data.value?.salesAggr) }}
              </a-tooltip>
            </template>
            <template #description>
              <a-row :gutter="[24, 24]" class="">
                <a-col span="12"> 
                  <a-skeleton
                    :loading="summary.isLoading.value"
                    :title="false"
                    :paragraph="{ rows: 1 }"
                    active
                    class="my-0"
                  >
                    {{ $tc('dashboard.total_orders', summary.data.value?.totalOrderAggr, { value: formatNumber(summary.data.value?.totalOrderAggr, false) }) }} 
                  </a-skeleton>
                </a-col>
                <a-col span="12 text-right"> 
                  <a-tooltip placement="bottom" :title="$t('dashboard.gmv.abv.long', { n: formatNumber(summary.data.value?.salesAggr / summary.data.value?.totalOrderAggr) })">
                    {{ $t('dashboard.gmv.abv.short', { n: abbrNumber(summary.data.value?.salesAggr / summary.data.value?.totalOrderAggr) }) }}
                  </a-tooltip>
                </a-col>
              </a-row>
            </template>
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <g fill="none" stroke="currentColor" stroke-width="1.5">
                  <path
                    stroke-linecap="round"
                    d="m8 16l8.72-.727c2.729-.227 3.341-.823 3.643-3.544L21 6M6 6h1.5M22 6h-3"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M10.5 7s1 0 2 2c0 0 3.177-5 6-6"
                  />
                  <circle cx="6" cy="20" r="2" />
                  <circle cx="17" cy="20" r="2" />
                  <path
                    stroke-linecap="round"
                    d="M8 20h7M2 2h.966c.945 0 1.768.625 1.997 1.515L7.94 15.076a1.96 1.96 0 0 1-.35 1.686L6.631 18"
                  />
                </g>
              </svg>
            </template>
          </MetricWidget>
        </a-col>
        <a-col :span="24" :lg="{ span: 12 }" :xl="{ span: 6 }">
          <MetricWidget
            :label="$t('dashboard.nmv.title')"
            :loading="summary.isLoading.value"
            rgb-color="33,150,243"
          >
            <template #value>
              <a-tooltip placement="bottom" :title="formatNumber(summary.data.value?.nmvAggr)">
                {{ abbrNumber(summary.data.value?.nmvAggr) }}
              </a-tooltip>
            </template>
            <template #description>
              <a-row :gutter="[24, 24]" class="">
                <a-col span="12"> 
                  <a-skeleton
                    :loading="summary.isLoading.value"
                    :title="false"
                    :paragraph="{ rows: 1 }"
                    active
                    class="my-0"
                  >
                    {{ $tc('dashboard.total_orders', summary.data.value?.totalOrderAggr, { value: formatNumber(summary.data.value?.totalOrderAggr, false) }) }} 
                  </a-skeleton>
                </a-col>
                <a-col span="12 text-right"> 
                  <a-tooltip placement="bottom" :title="$t('dashboard.nmv.abv.long', { n: formatNumber(summary.data.value?.nmvAggr / summary.data.value?.totalOrderAggr) })">
                    {{ $t('dashboard.nmv.abv.short', { n: abbrNumber(summary.data.value?.nmvAggr / summary.data.value?.totalOrderAggr) }) }}
                  </a-tooltip>
                </a-col>
              </a-row>
            </template>
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <g
                  fill="none"
                  stroke="currentColor"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                >
                  <path
                    stroke-linecap="round"
                    d="M20.943 16.835a15.76 15.76 0 0 0-4.476-8.616c-.517-.503-.775-.754-1.346-.986C14.55 7 14.059 7 13.078 7h-2.156c-.981 0-1.472 0-2.043.233c-.57.232-.83.483-1.346.986a15.76 15.76 0 0 0-4.476 8.616C2.57 19.773 5.28 22 8.308 22h7.384c3.029 0 5.74-2.227 5.25-5.165"
                  />
                  <path
                    d="M7.257 4.443c-.207-.3-.506-.708.112-.8c.635-.096 1.294.338 1.94.33c.583-.009.88-.268 1.2-.638C10.845 2.946 11.365 2 12 2s1.155.946 1.491 1.335c.32.37.617.63 1.2.637c.646.01 1.305-.425 1.94-.33c.618.093.319.5.112.8l-.932 1.359c-.4.58-.599.87-1.017 1.035S13.837 7 12.758 7h-1.516c-1.08 0-1.619 0-2.036-.164S8.589 6.38 8.189 5.8z"
                  />
                  <path
                    stroke-linecap="round"
                    d="M13.627 12.919c-.216-.799-1.317-1.519-2.638-.98s-1.53 2.272.467 2.457c.904.083 1.492-.097 2.031.412c.54.508.64 1.923-.739 2.304c-1.377.381-2.742-.214-2.89-1.06m1.984-5.06v.761m0 5.476v.764"
                  />
                </g>
              </svg>
            </template>
          </MetricWidget>
        </a-col>
        <a-col :span="24" :lg="{ span: 12 }" :xl="{ span: 6 }">
          <MetricWidget
            :label="$t('dashboard.logistic.title')"
            :loading="summary.isLoading.value"
            rgb-color="7, 190, 154"
          >
            <template #value>
              <a-tooltip placement="bottom" :title="formatNumber(summary.data.value?.logisticCost)">
                {{ abbrNumber(summary.data.value?.logisticCost) }}
              </a-tooltip>
            </template>
            <template #description>
              <a-row :gutter="[24, 24]" class="">
                <!-- <a-col span="12"> 189772 Orders </a-col> -->
                <a-col class="text-left">
                  <a-tooltip :title="$t('dashboard.logistic.als.long', { n: formatNumber(summary.data.value?.logisticCost / summary.data.value?.totalOrderAggr) })">
                    {{ $t('dashboard.logistic.als.short', { n: formatNumber(summary.data.value?.logisticCost / summary.data.value?.totalOrderAggr) }) }}
                  </a-tooltip>
                </a-col>
              </a-row>
            </template>
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <g
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                >
                  <path
                    d="M2 21.494c1.295.096 2.384-.994 3.333-.994s2.492 1.005 3.334.994c1.01.009 2.193-.994 3.333-.994s2.323 1.003 3.333.994c1.295.096 2.384-.994 3.334-.994s2.491 1.005 3.333.994M6 20.5c-1.418-1.766-2.417-4.027-2.843-5.225c-.135-.38-.202-.57-.124-.752c.079-.181.27-.266.655-.436l7.49-3.318c.404-.18.607-.269.822-.269s.418.09.823.27l7.489 3.317c.384.17.576.255.655.436c.078.182.01.372-.124.752c-.426 1.198-1.425 3.459-2.843 5.225"
                  />
                  <path
                    d="m6 13l.216-2.807c.135-1.752.202-2.628.777-3.16c.575-.533 1.454-.533 3.211-.533h3.592c1.757 0 2.636 0 3.21.533c.576.532.643 1.408.778 3.16L18 13"
                  />
                  <path
                    d="m8.5 6.5l.172-1.372c.156-1.251.234-1.877.66-2.253c.425-.375 1.055-.375 2.316-.375h.704c1.26 0 1.891 0 2.316.375c.426.376.504 1.002.66 2.253L15.5 6.5"
                  />
                </g>
              </svg>
            </template>
          </MetricWidget>
        </a-col>
        <a-col :span="24" :lg="{ span: 12 }" :xl="{ span: 6 }">
          <MetricWidget
            :label="$t('dashboard.item_sold')"
            :value="formatNumber(summary.data.value?.totalQtyAggr, false)"
            :loading="summary.isLoading.value"
            description="Total of pending Inbound Delivery"
            rgb-color="255,193,7"
          >
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <g
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                >
                  <path d="m16 16l2 2l4-4" />
                  <path
                    d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14M7.5 4.27l9 5.15"
                  />
                  <path d="M3.29 7L12 12l8.71-5M12 22V12" />
                </g>
              </svg>
            </template>
          </MetricWidget>
        </a-col>
      </a-row>

      <div class="mt-4">
        <div class="card" style="">
          <div class="mb-3 d-flex justify-content-between">
            <h2>{{ $t('dashboardContent.sales_overview_graph') }}</h2>
            <div v-if="!salesOverview.isLoading.value && salesOverviewTime.timeDiffInDays >= 30 && salesOverviewTime.timeDiffInDays <= 90">
              <a-radio-group v-model="salesTimeRange" size="small" class="text-small">
                <a-radio-button value="daily">
                  {{ $t('dashboard.daily') }}
                </a-radio-button>
                <a-radio-button value="weekly">
                  {{ $t('dashboard.weekly') }}
                </a-radio-button>
              </a-radio-group>
            </div>
          </div>
          <div class="">
            <a-skeleton
              active
              :loading="salesOverview.isLoading.value"
              :title="false"
              :paragraph="{ rows: 1 }"
              class="sales-overview-loading"
            >
              <LineChart
                :chart-data="salesChart"
                :options="salesChartConfig"
                style="height: 400px"
              />
            </a-skeleton>

            <a-divider class="my-5" />

            <div class="chart-grid">
              <div>
                <ReportChartByBusiness
                  :color-palette="colorPalette"
                  :filter="statisticsFilter"
                  :value-formatting="value => formatNumber(value, true, false)"
                />
              </div>
              <div>
                <ReportChartByWarehouse
                  :color-palette="colorPalette"
                  :filter="statisticsFilter"
                  :value-formatting="value => formatNumber(value, true, false)"
                />
              </div>
              <div>
                <ReportChartBySalesChannel
                  :color-palette="colorPalette"
                  :filter="statisticsFilter"
                  :value-formatting="value => formatNumber(value, true, false)"
                />
              </div>
              <div>
                <ReportChartByChannel
                  :color-palette="colorPalette"
                  :filter="statisticsFilter"
                  :value-formatting="value => formatNumber(value, true, false)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <a-row :gutter="[24, 20]" class="mt-3">
        <a-col span="24" :lg="{ span: 12 }">
          <div class="card">
            <ReportChartByArea :filter="statisticsFilter" :value-formatting="(value) => formatNumber(value, false, false)" />
          </div>
        </a-col>
        <a-col span="24" :lg="{ span: 12 }">
          <div class="card">
            <ReportChartByProduct :filter="statisticsFilter" :value-formatting="(value) => formatNumber(value, false, false)" />
          </div>
        </a-col>
      </a-row>
    </div>
  </div>
</template>

<style lang="scss" scoped>

::v-deep {
  h2,
  .title {
    font-size: 1.25rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  .subtitle {
    font-size: 0.875rem;
    font-weight: 400;
    color: var(--text-muted);
    margin-top: -.5rem;
    margin-bottom: 1rem;
  }
}


.card {
  padding: 1.25rem;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -5px rgba(0, 0, 0, 0.075);
  border: 0;

  h2 {
    margin-bottom: 1.5rem;
  }
}

.ant-select::v-deep .ant-select-selection__placeholder,
.ant-select::v-deep .ant-select-search__field__placeholder {
  color: var(--text-color-secondary);
}

.ant-select::v-deep
  .ant-select-selection--multiple
  .ant-select-selection__rendered {
  height: 32px;
}

.ant-skeleton::v-deep {
  ul {
    margin: 0;
  }
}

.summary-section {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  grid-template-areas:
    'order '
    '.';
  gap: 2.5rem 1.25rem;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-areas:
      'order order'
      '. .';
  }

  @media screen and (min-width: 1200px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-template-areas:
      'order order . .'
      'order order . .';
  }

  > * {
    // border: 1px solid red;
    min-height: 50px;
    flex-grow: 0;
    flex-shrink: 0;
  }

  .order-status {
    --col-num: 2;
    background: #fff;
    border-radius: 0.5rem;
    padding: 1rem;
    box-shadow: 0 10px 15px -5px rgba(0, 0, 0, 0.075);

    h2 {
      font-size: 1.25rem;
    }

    ul {
      display: grid;
      grid-template-columns: repeat(var(--col-num), minmax(0, 1fr));
      list-style-type: none;
      margin: 0;
      padding: 0;
      height: 100%;

      li {
        padding: 1rem 0.75rem;
        display: flex;
        justify-content: center;
        text-align: center;
      }

      li:not(:nth-last-child(-n + 2)) {
        border-bottom: 1px solid #ededed;
      }

      li:not(:nth-child(2n)) {
        border-right: 1px solid #ededed;
      }

      li a {
      }
    }

    &__title {
      font-size: 92%;
      color: #777777;
      font-weight: 400;
      letter-spacing: 0.5px;
    }

    &__value {
      font-size: 1.25rem;
      font-weight: 500;
      color: #1c1c1c;
      margin-bottom: 0.25rem;
      position: relative;
      display: inline-block;

      &:before {
        content: '';
        width: 130%;
        height: 50%;
        position: absolute;
        background: rgba(var(--color, 255, 10, 84), 0.15);
        bottom: 2%;
        left: 50%;
        transform: translateX(-50%) skewX(0.312rad);
        min-width: 50px;
      }

      > span {
        position: relative;
      }
    }
  }
}

.is-active {
  position: relative;

  &:before {
    width: calc(100% + 4px);
    height: calc(100% + 4px);
    position: absolute;
    background: var(--kit-color-primary);
    transform: translate(-50%, -50%);
    content: '';
    left: 50%;
    top: 50%;
    border-radius: .35rem;
    opacity: .35;
    z-index: 0;
  }

  &::v-deep {
    > * {
      position: relative;
      z-index: 1;
    }
  }
}

.statistics-overview-section {
  .filter-form {
    .date-fields {
      display: flex;
      align-items: center;
      gap: 1rem;

      ul {
        display: flex;
        width: 100%;
        flex-wrap: wrap;
        list-style-type: none;
        margin: 0 auto 0 0;
        padding: 0;
        gap: 0.5rem;

        > li {
          flex-shrink: 0;
        }

        > li:not(.date-picker) {
          cursor: pointer;
          border: 1px solid var(--border-light);
          border-radius: 4px;
          padding: 5px 10px;
          background-color: #fff;
          color: var(--text-color-secondary);
          flex-shrink: 0;
          display: none;

          @media screen and (min-width: 768px) {
            display: block;
          }
        }

        > li.date-picker {
          margin-left: auto;
          width: 100%;
          
          @media screen and (min-width: 768px) {
            width: auto;
          }
        }

        > li.active {
          background: var(--color-accent);
          color: #ffffff;
          border-color: var(--color-accent);
          box-shadow: 0 4px 15px -2px rgba(255, 10, 84, 0.1);
        }
      }
    }
  }

  .ant-radio-group-small {
    font-size: 0.8rem;

    ::v-deep label:not(.ant-radio-button-wrapper-checked) {
      color: var(--text-color-secondary);
    }
  }

  .sales-overview-loading::v-deep {
    .ant-skeleton-paragraph li {
      width: 100% !important;
      height: 400px !important;
    }
  }

  ::v-deep {
    .chart-container {
      margin-top: -1rem;
      flex-direction: column;
      gap: 2rem;


      @media screen and (min-width: 500px) and (max-width: 767px) {
        flex-direction: row;
      }

      @media screen and (min-width: 1024px) {
        margin-top: -1.5rem;
        flex-direction: row;
      }
    }

    .chart-container label {
      color: #8484ac;

      &.disabled {
        color: #8484ac70;
      }
    }
  }
}

.chart-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 4rem 1rem;
  margin-bottom: 2rem;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding-left: 1rem;
    padding-right: 1rem;
  }

  @media screen and (min-width: 2200px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.currency-input::v-deep {
  width: 100%;
  margin-bottom: 1.5rem;

  @media screen and (min-width: 992px) {
    width: auto; 
    margin-bottom: 0;
  }

  .ant-select-selection {
    height: 44px;
  }

  .ant-select-selection-selected-value {
    padding: 6px 12px 8px 8px;
  }
} 
</style>
